import {
  Button,
  Center,
  CloseButton,
  Drawer,
  Group,
  Loader,
  Text,
} from "@mantine/core"
import { modals } from "@mantine/modals"
import { useCallback, useRef } from "react"
import { Trans, useTranslation } from "react-i18next"
import { useCrudItem } from "../../../hooks/useCrud"
import { useUser } from "../../user/hooks/useUser"
import { useDeleteDynamicDropdown } from "../hooks/useDynamicDropdowns"
import type { DynamicDropdown } from "../models/dynamicDropdown"
import { DynamicDropdownSchema } from "../models/dynamicDropdown"
import DynamicDropdownDetailContent from "./DynamicDropdownDetailContent"
import DynamicDropdownFormContent from "./DynamicDropdownFormContent"

export type DrawerMode = "view" | "create" | "edit"

interface Props {
  opened: boolean
  onClose: () => void
  mode: DrawerMode
  selectedId: number | null
  onDeleted: () => void
  onSwitchToEdit: () => void
}

export default function DynamicDropdownDrawer({
  opened,
  onClose,
  mode,
  selectedId,
  onDeleted,
  onSwitchToEdit,
}: Props) {
  const { t } = useTranslation()
  const { data: item, isLoading } = useCrudItem<DynamicDropdown>(
    "dynamic-dropdowns",
    "dynamic-dropdown",
    DynamicDropdownSchema,
    mode !== "create" ? selectedId : null,
  )
  const { data: userData } = useUser()
  const canManage = userData?.can?.viewMenuAdmin ?? false
  const deleteMutation = useDeleteDynamicDropdown()
  const formRef = useRef<{ submit: () => void }>(null)
  const isFormMode = mode === "edit" || mode === "create"

  const handleDelete = useCallback(() => {
    if (!item?.id) return
    modals.openConfirmModal({
      title: t("dynamicDropdowns.confirmDelete"),
      children: (
        <Text size="sm">{t("dynamicDropdowns.confirmDeleteMessage")}</Text>
      ),
      labels: { confirm: t("common.delete"), cancel: t("common.cancel") },
      confirmProps: { color: "red" },
      onConfirm: () => {
        deleteMutation.mutate(item.id, { onSuccess: () => onDeleted() })
      },
    })
  }, [item, deleteMutation, onDeleted, t])

  const title =
    mode === "create"
      ? t("dynamicDropdowns.createTitle")
      : mode === "edit"
        ? t("dynamicDropdowns.editTitle")
        : (item?.option ?? "")

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      position="right"
      size="md"
      withCloseButton={false}
    >
      <Drawer.Header>
        <Drawer.Title>
          <Text fw={600} size="md" lineClamp={1}>
            {title}
          </Text>
        </Drawer.Title>
        <Group gap="xs">
          {mode === "view" && canManage && (
            <>
              <Button
                size="xs"
                variant="default"
                onClick={onSwitchToEdit}
                leftSection={<i className="fa fa-pencil" />}
              >
                <Trans i18nKey="common.edit" />
              </Button>
              <Button
                size="xs"
                color="red"
                onClick={handleDelete}
                leftSection={<i className="fa fa-trash" />}
              >
                <Trans i18nKey="common.delete" />
              </Button>
            </>
          )}
          {isFormMode && (
            <Button size="xs" onClick={() => formRef.current?.submit()}>
              <Trans
                i18nKey={mode === "edit" ? "common.update" : "common.create"}
              />
            </Button>
          )}
          <CloseButton onClick={onClose} />
        </Group>
      </Drawer.Header>

      {mode === "create" ? (
        <DynamicDropdownFormContent ref={formRef} onSuccess={onClose} />
      ) : isLoading ? (
        <Center h="100%">
          <Loader />
        </Center>
      ) : !item ? (
        <Text p="md">
          <Trans i18nKey="common.notFound" />
        </Text>
      ) : mode === "edit" ? (
        <DynamicDropdownFormContent
          ref={formRef}
          item={item}
          onSuccess={onClose}
        />
      ) : (
        <DynamicDropdownDetailContent item={item} />
      )}
    </Drawer>
  )
}
